import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Constants } from 'src/app/constants';

declare let gtag: Function;

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  /** Store context of the current session — used for the PostHog person display name */
  private currentStoreId: any = null;
  private currentStoreName: string | null = null;
  /** Phone number of the identified customer, null while anonymous */
  private identifiedPhone: string | null = null;

  private get gaEnabled(): boolean {
    return !!(environment as any).gaTrackingId;
  }

  private get posthogEnabled(): boolean {
    return !!(environment as any).posthogKey;
  }

  private get posthog(): any {
    return (window as any).posthog;
  }

  /** Initialize PostHog (called once from AppComponent) */
  initPostHog(): void {
    const key = (environment as any).posthogKey;
    const host = (environment as any).posthogHost || 'https://us.i.posthog.com';
    if (!key) return;

    // Load PostHog snippet
    (function(t,e){var o,n,p,r;e.__SV||((window as any).posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)})(document,(window as any).posthog||[]);

    (window as any).posthog.init(key, {
      api_host: host,
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: true,
      // Unhandled errors + rejected promises land in PostHog Error Tracking. Angular swallows
      // most of its own errors, so PosthogErrorHandler forwards those in as well.
      capture_exceptions: true,
      session_recording: {
        // Customers type phone numbers, emails, delivery addresses and special instructions
        // into this app — mask every input by default and opt individual ones back in.
        maskAllInputs: true,
        maskInputFn: (text: string, element?: HTMLElement) => {
          try {
            if (element && element.closest('[data-ph-unmask]')) return text;
          } catch (e) { }
          return '*'.repeat((text || '').length);
        },
        // Rendered (non-input) PII — e.g. the address on the confirmation screen
        maskTextSelector: '[data-ph-mask]'
      }
    });

    this.restoreIdentity();
  }

  /**
   * A verified phone number survives reloads in localStorage, so re-apply the identity
   * on every app boot — otherwise a returning customer shows up as an anonymous person.
   */
  private restoreIdentity(): void {
    try {
      const phone = localStorage.getItem(Constants.PHONE_NUMBER_KEY);
      if (phone) {
        this.identifyUser(null, phone);
      }
    } catch (e) { }
  }

  /** Normalize to +1XXXXXXXXXX so the same customer maps to one person everywhere */
  private normalizePhone(phoneNumber: string): string {
    const digits = (phoneNumber || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
    return '+' + digits;
  }

  /** "Store Name - +1XXXXXXXXXX" — what PostHog shows in the PERSON column */
  private buildDisplayName(phone: string): string {
    return this.currentStoreName ? `${this.currentStoreName} - ${phone}` : phone;
  }

  private personProperties(phone: string): Record<string, any> {
    const properties: Record<string, any> = {
      // PostHog picks `name` for the person display name by default
      name: this.buildDisplayName(phone),
      phone_number: phone
    };
    if (this.currentStoreName) {
      properties['store_name'] = this.currentStoreName;
    }
    if (this.currentStoreId) {
      properties['store_id'] = this.currentStoreId;
    }
    return properties;
  }

  /**
   * Set user identity after login/OTP verification.
   * The phone number is the distinct id in PostHog so the login dialog, checkout OTP and
   * the offer flows all resolve to the same person instead of one person per flow.
   * `userId` (phone hash) is only used for GA4, which must not receive raw phone numbers.
   */
  identifyUser(userId: string | null, phoneNumber?: string): void {
    const phone = phoneNumber ? this.normalizePhone(phoneNumber) : '';

    if (this.gaEnabled && userId) {
      gtag('config', (environment as any).gaTrackingId, { user_id: userId });
    }
    if (this.posthogEnabled && this.posthog) {
      const distinctId = phone || userId;
      if (!distinctId) return;
      this.identifiedPhone = phone || null;
      // These calls sit inside the checkout OTP path — analytics must never break an order.
      try {
        // posthog-js refuses to switch identity once identified, so a browser still carrying the
        // old phone-hash id — or a shared device where a different customer verifies — would keep
        // sending events to the wrong person. Reset first, then identify.
        if (this.posthog._isIdentified && this.posthog._isIdentified() &&
            this.posthog.get_distinct_id && this.posthog.get_distinct_id() !== distinctId) {
          this.posthog.reset();
        }
        this.posthog.identify(distinctId, phone ? this.personProperties(phone) : {});
      } catch (e) { }
    }
  }

  /** Set current store context — attaches to all subsequent events */
  setStore(storeId: any, storeName: string): void {
    this.currentStoreId = storeId;
    this.currentStoreName = storeName;
    if (this.posthogEnabled && this.posthog) {
      try {
        this.posthog.register({
          current_store_id: storeId,
          current_store_name: storeName
        });
        // The store usually loads after we already know the customer — refresh the
        // display name so it reads "Store Name - phone" instead of just the phone.
        if (this.identifiedPhone) {
          this.posthog.setPersonProperties(this.personProperties(this.identifiedPhone));
        }
      } catch (e) { }
    }
  }

  /** Clear user identity on logout */
  clearUser(): void {
    if (this.gaEnabled) {
      gtag('config', (environment as any).gaTrackingId, { user_id: null });
    }
    if (this.posthogEnabled && this.posthog) {
      this.posthog.reset();
    }
    this.identifiedPhone = null;
  }

  /** Track page views (called from router events) */
  trackPageView(url: string): void {
    if (this.gaEnabled) {
      gtag('config', (environment as any).gaTrackingId, { page_path: url });
    }
    if (this.posthogEnabled && this.posthog) {
      this.posthog.capture('$pageview', { $current_url: url });
    }
  }

  /**
   * Report a caught error to PostHog Error Tracking.
   * Used by PosthogErrorHandler and anywhere we swallow an error but still want visibility.
   */
  captureException(error: any, context?: Record<string, any>): void {
    if (!this.posthogEnabled || !this.posthog) return;
    try {
      const err = error instanceof Error ? error : new Error(String(error && error.message || error));
      this.posthog.captureException(err, context || {});
    } catch (e) { }
  }

  /**
   * Fire an event at most once per `dedupeKey` on this device.
   * The order-status page re-mounts on every reload and revisit, which was reporting the same
   * order as a fresh `purchase` up to 12 times in one session and inflating revenue.
   */
  trackEventOnce(eventName: string, dedupeKey: string, params?: Record<string, any>): void {
    const storageKey = `analytics_once_${eventName}_${dedupeKey}`;
    try {
      if (localStorage.getItem(storageKey)) return;
      localStorage.setItem(storageKey, '1');
    } catch (e) {
      // Private mode / blocked storage: better a duplicate event than a missing one.
    }
    this.trackEvent(eventName, params);
  }

  /** Track custom events — sends to both GA4 + PostHog */
  trackEvent(eventName: string, params?: Record<string, any>): void {
    if (this.gaEnabled) {
      gtag('event', eventName, params || {});
    }
    if (this.posthogEnabled && this.posthog) {
      this.posthog.capture(eventName, params || {});
    }
  }
}
